import classNames from "classnames";
import React from "react";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { ProductSchemaTag } from "../../../common/ProductSchemaTag";
import { ProductCompareTheme } from "../../../constants";
import { IWebPageURL } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
    IProductTileAttributeRow,
    ITileID,
    isoTileID,
} from "../../../models/product-compare";
import { ProductCompareTileAttributeRow } from "../elements/ProductCompareTileAttributeRow";
import { ProductCompareTileCTA } from "../elements/ProductCompareTileCTA";
import { ProductCompareTileHeader } from "../elements/ProductCompareTileHeader";
import { getSelectedVariant } from "../selectors";
import { setShowStartingAt } from "../utility";

import styles from "./ProductSelectionGridColumn.module.scss";

const removeFromURLParams = (tileID: ITileID): void => {
    const window = getWindow();
    if (!window) {
        return;
    }
    const history = window.history;
    const urlParams = new URLSearchParams(window.location.search);
    const selectedTileIDs = decodeURIComponent(urlParams.get("tiles") || "")
        .split(",")
        .map(Number)
        .map(isoTileID.wrap);
    const newTileIDs = selectedTileIDs.filter((id) => id !== tileID);

    if (newTileIDs.length > 0) {
        urlParams.set("tiles", newTileIDs.join(","));
        const newURL = `${window.location.origin}${
            window.location.pathname
        }?${urlParams.toString()}`;
        history.replaceState(history.state, "", newURL);
    } else {
        urlParams.delete("tiles");
        const newURL = `${window.location.origin}${
            window.location.pathname
        }?${urlParams.toString()}`;
        history.replaceState(history.state, "", newURL);
    }
};

const addToURLParams = (tileID: ITileID): void => {
    const window = getWindow();
    if (!window) {
        return;
    }
    const history = window.history;
    const urlParams = new URLSearchParams(window.location.search);
    let selectedTileIDs: ITileID[] = [];
    if (urlParams.get("tiles")) {
        selectedTileIDs = decodeURIComponent(urlParams.get("tiles") || "")
            .split(",")
            .map(Number)
            .map(isoTileID.wrap);
    }
    const newTileIDs = [...selectedTileIDs, tileID];

    urlParams.set("tiles", newTileIDs.join(","));
    const newURL = `${window.location.origin}${
        window.location.pathname
    }?${urlParams.toString()}`;
    history.replaceState(history.state, "", newURL);
};

const isChecked = (
    tile: ICompareTile,
    selectedTileIDs: readonly ITileID[],
): boolean => {
    return selectedTileIDs.includes(tile.product_compare_tile.id);
};

const buildCompareCount = (
    selectedTileIDs: readonly ITileID[],
): string | null => {
    if (selectedTileIDs.length > 0) {
        return `(${selectedTileIDs.length})`;
    } else {
        return null;
    }
};

const buildCompareNowURI = (
    baseURL: IWebPageURL,
    selectedTileIDs: readonly ITileID[],
): string => {
    const window = getWindow();
    const returnURL = encodeURIComponent(window?.location.pathname ?? "/");
    const ids = encodeURIComponent(selectedTileIDs.join(","));
    return `${baseURL}?tiles=${ids}&return_url=${returnURL}`;
};

const buildAttributeRow = (
    tile: ICompareTile,
    financingLink: IWebPageURL,
    sizeAttributeOptionGroup: IAttributeOptionGroup | null,
    highestAttributeCount?: number,
): React.ReactNode => {
    return tile.rows.map((row: IProductTileAttributeRow) => (
        <ProductCompareTileAttributeRow
            key={`${tile.product_compare_tile.id}-${row.row_type.id}`}
            row={row}
            data={tile}
            financingLink={financingLink}
            sizeAttributeOptionGroup={sizeAttributeOptionGroup}
            highestAttributeCount={highestAttributeCount}
        />
    ));
};

export const ProductSelectionGridColumn = (props: {
    tile: ICompareTile;
    showStartingAt?: boolean;
    showRating?: boolean;
    showInStoreCallout?: boolean;
    productComparePageURL: IWebPageURL | null;
    selectedTileIDs: readonly ITileID[];
    priceSelectValue: string;
    financingLink: IWebPageURL;
    sizeAttributeOptionGroup: IAttributeOptionGroup | null;
    theme: ProductCompareTheme;
    highestAttributeCount?: number;
    addToSelected: (tileID: ITileID) => void;
    removeFromSelected: (tileID: ITileID) => void;
    showModal: () => void;
}): React.ReactNode => {
    const onCompareCheckboxChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        tile: ICompareTile,
    ): void => {
        if (e.currentTarget.checked) {
            if (props.selectedTileIDs.length < 4) {
                props.addToSelected(tile.product_compare_tile.id);
                addToURLParams(tile.product_compare_tile.id);
            } else {
                props.showModal();
            }
        } else {
            props.removeFromSelected(tile.product_compare_tile.id);
            removeFromURLParams(tile.product_compare_tile.id);
        }
    };

    const showCompareFunctionality = [
        ProductCompareTheme.WITHOUT_COMPARE,
        ProductCompareTheme.THEME_ONE,
    ].includes(props.theme)
        ? false
        : true;
    const variant = getSelectedVariant(
        props.tile,
        props.sizeAttributeOptionGroup ? props.priceSelectValue : null,
    );
    const tileClasses = classNames({
        ["tile"]: true,
        [styles.tile]: props.theme === ProductCompareTheme.DEFAULT,
        [styles.tileThemeWithoutCompare]:
            props.theme === ProductCompareTheme.WITHOUT_COMPARE,
        [styles.tileThemeOne]: props.theme === ProductCompareTheme.THEME_ONE,
        [props.theme || ""]: true,
        ["hidden"]: props.tile.is_promo_period_active === false,
    });
    const highlightedAttributesClasses = classNames({
        [styles.highlightedAttributes]:
            props.theme === ProductCompareTheme.DEFAULT,
        [styles.highlightedAttributesThemeWithoutCompare]:
            props.theme === ProductCompareTheme.WITHOUT_COMPARE,
        [styles.highlightedAttributesThemeOne]:
            props.theme === ProductCompareTheme.THEME_ONE,
    });
    return (
        <div
            className={tileClasses}
            role="group"
            aria-label={t`Mattress Model`}
        >
            <ProductCompareTileHeader
                data={props.tile}
                selectedVariant={variant}
                showDescription={true}
                showRating={
                    props.showRating !== undefined ? props.showRating : true
                }
                theme={props.theme}
            />
            <section className={highlightedAttributesClasses}>
                {buildAttributeRow(
                    props.tile,
                    props.financingLink,
                    props.sizeAttributeOptionGroup,
                    props.highestAttributeCount,
                )}
            </section>
            <ProductCompareTileCTA
                data={props.tile}
                selectedVariant={variant}
                showInStoreCallout={props.showInStoreCallout}
                showStartingAt={
                    props.showStartingAt
                        ? props.showStartingAt
                        : setShowStartingAt(
                              props.tile,
                              props.sizeAttributeOptionGroup,
                          )
                }
                financingLink={props.financingLink}
                theme={props.theme}
            />
            {showCompareFunctionality && (
                <form className={styles.form}>
                    <span className="ada-screenreader-only">
                        {t`Activating these elements will cause content on the page to be updated. Please go to the Compare section of the page to view your results.`}
                    </span>
                    <input
                        id={`product-compare-tile-selected-${props.tile.product_compare_tile.id}`}
                        onChange={(e) => {
                            onCompareCheckboxChange(e, props.tile);
                        }}
                        type="checkbox"
                        className="al-comparisongrid-checked"
                        checked={isChecked(props.tile, props.selectedTileIDs)}
                    />
                    <label
                        htmlFor={`product-compare-tile-selected-${props.tile.product_compare_tile.id}`}
                    >
                        {isChecked(props.tile, props.selectedTileIDs)
                            ? `Selected`
                            : `Compare`}
                    </label>
                </form>
            )}
            {isChecked(props.tile, props.selectedTileIDs) &&
                showCompareFunctionality &&
                props.productComparePageURL && (
                    <a
                        href={buildCompareNowURI(
                            props.productComparePageURL,
                            props.selectedTileIDs,
                        )}
                        className="al-comparsiongrid-compareselected"
                    >
                        Compare Now{" "}
                        <span>{buildCompareCount(props.selectedTileIDs)}</span>
                    </a>
                )}
            {variant && (
                <ProductSchemaTag
                    name={variant.title}
                    imageUrl={variant.images[0] && variant.images[0].original}
                    description={variant.description}
                    sku={variant.upc}
                    brandName="Tempur-Pedic"
                    ratingValue={variant.rating}
                    reviewCount={variant.num_reviews}
                />
            )}
        </div>
    );
};
